import { useEffect, useRef, useState, useCallback } from 'preact/hooks'
import { lookupsApi } from '@/api'
import type { ReplyToAddressLookupDto } from '@/types/lookups'

// Module-level cache keyed by workspaceId. The book changes only via the Settings screen, which calls
// invalidateReplyToAddressesLookup() on every mutation, so this cache is never stale across screens.
const cache = new Map<string, ReplyToAddressLookupDto[]>()
const inFlight = new Map<string, Promise<ReplyToAddressLookupDto[] | null>>()

/**
 * Clears the cached reply-to lookups so the next read refetches. Called after the address book is
 * mutated in Settings (see useReplyToAddresses) so the activation editor never shows a stale option set.
 * Clears all workspaces because the management screen is company-scoped and has no workspaceId in hand.
 */
export function invalidateReplyToAddressesLookup(): void {
  cache.clear()
  inFlight.clear()
}

export interface UseReplyToAddressesLookupResult {
  data: ReplyToAddressLookupDto[] | null
  isLoading: boolean
  error: string | null
  refetch: () => void
}

/** Loads the company's reply-to addresses (resolved from the workspace) as dropdown options. */
export function useReplyToAddressesLookup(workspaceId: string | undefined): UseReplyToAddressesLookupResult {
  const key = workspaceId || null
  const [data, setData] = useState<ReplyToAddressLookupDto[] | null>(key ? cache.get(key) ?? null : null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Generation token: only the latest fetch (for the current workspaceId, while mounted) applies its
  // result, so a superseded workspace switch or an unmount can't write stale data.
  const genRef = useRef(0)

  const fetchAddresses = useCallback(async (force = false) => {
    if (!workspaceId) {
      setData(null)
      // Clear loading/error too — otherwise switching away mid-fetch (with the generation guard
      // blocking the in-flight finally) would leave the hook stuck loading until remount.
      setError(null)
      setIsLoading(false)
      return
    }
    const cacheKey = workspaceId
    const gen = ++genRef.current

    if (!force && cache.has(cacheKey)) {
      setData(cache.get(cacheKey) ?? null)
      return
    }

    const existing = inFlight.get(cacheKey)
    if (existing && !force) {
      const result = await existing
      if (genRef.current === gen && result) setData(result)
      return
    }

    setIsLoading(true)
    setError(null)

    const promise = lookupsApi.getReplyToAddresses(workspaceId).then(response => {
      if (response.error || !response.data) {
        if (genRef.current === gen) setError(response.error || 'Failed to load reply-to addresses')
        return null
      }
      cache.set(cacheKey, response.data)
      return response.data
    }).finally(() => {
      inFlight.delete(cacheKey)
      if (genRef.current === gen) setIsLoading(false)
    })

    inFlight.set(cacheKey, promise)
    const result = await promise
    if (genRef.current === gen && result) setData(result)
  }, [workspaceId])

  useEffect(() => {
    void fetchAddresses()
    // Bump the token on unmount / workspace change so any in-flight result is dropped.
    return () => { genRef.current++ }
  }, [fetchAddresses])

  const refetch = useCallback(() => {
    if (key) cache.delete(key)
    void fetchAddresses(true)
  }, [key, fetchAddresses])

  return { data, isLoading, error, refetch }
}
